import { useState, useEffect } from 'react'
import humanizeDuration, { HumanizerOptions } from 'humanize-duration'
import dayjs from 'dayjs'
import isNil from 'lodash/isNil'
import { useGetCycleConfigQuery } from 'state/services/indexNode/indexNode'
import { CycleConfig } from '@mprolab/sdk'

export const MILLISECONDS_PER_SECOND = 1000
export const CYCLE_START_HOUR = 8

export enum DURATION_LANGUAGE {
  SHORT_EN = 'shortEn',
  SHORT_EN_UPPER_CASE = 'shortEnUpperCase',
  SHORT_EN_EXTENDED = 'shortEnExtended'
}

// Duration in seconds
export const getDurationInMilliseconds = (duration?: number): number =>
  duration ? duration * MILLISECONDS_PER_SECOND : 0

export const displayDuration = ({ time, options }: { time: number; options?: HumanizerOptions }): string => {
  const humanizer = humanizeDuration.humanizer({
    units: ['d', 'h', 'm'],
    maxDecimalPoints: 0,
    spacer: '',
    delimiter: ' ',
    largest: 3,
    language: DURATION_LANGUAGE.SHORT_EN,
    languages: {
      [DURATION_LANGUAGE.SHORT_EN]: {
        y: () => 'y',
        mo: () => 'mo',
        w: () => 'w',
        d: () => 'd',
        h: () => 'h',
        m: () => 'm',
        s: () => 's',
        ms: () => 'ms'
      },
      [DURATION_LANGUAGE.SHORT_EN_UPPER_CASE]: {
        y: () => 'Y',
        mo: () => 'MO',
        w: () => 'W',
        d: () => 'D',
        h: () => 'H',
        m: () => 'M',
        s: () => 'S',
        ms: () => 'MS'
      },
      [DURATION_LANGUAGE.SHORT_EN_EXTENDED]: {
        y: () => 'y',
        mo: () => 'mo',
        w: () => 'w',
        d: () => 'd',
        h: () => 'h',
        m: () => 'min',
        s: () => 's',
        ms: () => 'ms'
      }
    }
  })

  if (options) {
    Object.entries(options).forEach(([key, value]) => {
      humanizer[key] = value
    })
  }
  return humanizer(time)
}

export const getEndOfCurrentCycle = ({
  cycleStartTimestamp,
  cycleDuration
}: {
  cycleStartTimestamp: number
  cycleDuration: number
}): dayjs.Dayjs => {
  // Get current time
  const currentUnixTime: number = dayjs().unix()
  // Calculate time difference and full cycles count
  const workingDiff: number = currentUnixTime - cycleStartTimestamp
  // Calculate full cycles count
  const fullCyclesCount = Math.floor(workingDiff / cycleDuration)
  // Calculate full cycles duration
  const fullCyclesDuration = fullCyclesCount * cycleDuration
  // Calculate end of current cycle
  const endOfCycleTimestamp = cycleStartTimestamp + fullCyclesDuration + cycleDuration
  const endOfCycle = dayjs.unix(endOfCycleTimestamp)

  return endOfCycle
}

export const getTimeUntilEndOfCycle = ({
  endOfCycle,
  cycleDuration
}: {
  endOfCycle: dayjs.Dayjs
  cycleDuration: number
}): {
  timeUntilEnd: number
  progress: number
} => {
  const currentTime = dayjs()
  // Calculate the time difference in milliseconds
  const timeDifference = endOfCycle.diff(currentTime, 'millisecond')

  const cycleDurationInMiliseconds = getDurationInMilliseconds(cycleDuration)
  const clampedTimeDifference = Math.max(0, Math.min(timeDifference, cycleDurationInMiliseconds))
  // Display elapsed cycle progress so each new cycle starts from zero.
  const currentProgress = ((cycleDurationInMiliseconds - clampedTimeDifference) / cycleDurationInMiliseconds) * 100

  return { timeUntilEnd: timeDifference, progress: currentProgress }
}

export const sumNumbers = (numbers: number[]): number => {
  return numbers.reduce((total, number) => total + number, 0)
}

export const useCycleDurationTimer = (): {
  timeUntilEnd: number
  progress: number
  endOfCycle: dayjs.Dayjs
  cycleConfig: CycleConfig
} => {
  const [endOfCycle, setEndOfCycle] = useState<dayjs.Dayjs>(null)
  const [timeUntilEnd, setTimeUntilEnd] = useState<number>(0)
  const [progress, setProgress] = useState<number>(0)
  const { data: cycleConfig, isSuccess } = useGetCycleConfigQuery(undefined)
  const calculateHoursLeft = (): void => {
    const { timeUntilEnd: timeDifference, progress } = getTimeUntilEndOfCycle({
      endOfCycle,
      cycleDuration: cycleConfig.cycleDuration
    })

    // If the difference is 0, update the endOfCycle
    if (timeDifference <= 0)
      setEndOfCycle(
        getEndOfCurrentCycle({
          cycleDuration: cycleConfig.cycleDuration,
          cycleStartTimestamp: cycleConfig.cycleStartTimestamp
        })
      )

    setTimeUntilEnd(timeDifference)
    setProgress(progress)
  }

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | undefined

    if (!isNil(endOfCycle)) {
      calculateHoursLeft()

      intervalId = setInterval(calculateHoursLeft, 1000)
    }

    return () => {
      if (intervalId !== undefined) clearInterval(intervalId)
    }
  }, [endOfCycle])

  useEffect(() => {
    if (isSuccess)
      setEndOfCycle(
        getEndOfCurrentCycle({
          cycleDuration: cycleConfig.cycleDuration,
          cycleStartTimestamp: cycleConfig.cycleStartTimestamp
        })
      )
  }, [isSuccess])

  return { timeUntilEnd, progress, endOfCycle, cycleConfig }
}

export const formatPrice = (number: number): string => {
  let price: string = number.toString()
  if (!Number.isInteger(number)) price = number.toFixed(6)

  return price
}

export const formatCycleTimestampUtc = (timestamp: number): string => dayjs.unix(timestamp).utc().format('MM/DD/YY, HH:mm [UTC]')
